import styles from './RentalSplitModal.module.css';

import type { FC } from 'react';
import type { DealChain } from 'src/utils/dealChain';
import type { ApiRentalDealItem } from 'src/types/rental';

import React, { useState } from 'react';
import { Warning, Handshake, CheckCircle } from '@phosphor-icons/react';

import { rentalContractCall } from 'src/utils/rentalActions';
import { rentalToken, formatBaseUnits, parseTokenAmount } from 'src/utils/rentalInvoice';

import { useWalletUI, WalletButton } from 'src/blockchain';

import { Modal } from 'src/components/Modal';
import { Stack } from 'src/components/themed/Stack';
import { Typography } from 'src/components/themed/Typography';
import { TxProgress, useTxProgress, useClearWhenWalletConnects } from 'src/components/TxProgress';

interface RentalSplitModalProps {
  visible: boolean;
  onClose: () => void;
  rental: ApiRentalDealItem;
  chain: DealChain | null;
  /** What is left to split, in base units — the contract rejects any proposal above it. */
  remainingDeposit: bigint;
  /** Called after proposeSplit() succeeds so the screen can refetch. */
  onProposed?: () => void;
}

/**
 * The renter's own proposal for a disputed deposit (`proposeSplit`, onchain §3.2).
 *
 * The number entered is **what the dealer keeps**; the rest comes back to the renter, and the
 * screen says so as the number is typed — a split is two amounts, and asking for one of them
 * without showing the other is how somebody proposes the opposite of what they meant.
 *
 * Nothing moves on one proposal. The split executes the moment a second party publishes the same
 * number, which is why agreeing with the dealer's proposal is the same call with their amount.
 * There is no approve step: the money is already inside the contract.
 */
export const RentalSplitModal: FC<RentalSplitModalProps> = ({
  visible,
  onClose,
  rental,
  chain,
  remainingDeposit,
  onProposed,
}) => {
  const walletUI = useWalletUI(chain?.family);
  const tx = useTxProgress();

  useClearWhenWalletConnects(tx, chain?.family);

  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const token = rentalToken(rental, chain);
  const contractAddress = rental.rentalContractState?.address ?? rental.contractAddress ?? '';
  const format = (value: bigint) => `${formatBaseUnits(value, token.decimals)} ${token.symbol}`;

  const rentierAmount = parseTokenAmount(input, token.decimals);
  const tooHigh = rentierAmount !== null && rentierAmount > remainingDeposit;
  const canSubmit = rentierAmount !== null && !tooHigh;
  const isProcessing = tx.isRunning && !success;

  const handleClose = () => {
    if (isProcessing) {
      return;
    }
    setError('');
    setSuccess(false);
    tx.reset();
    onClose();
  };

  const handleConfirm = async () => {
    if (isProcessing || rentierAmount === null) {
      return;
    }

    setError('');

    if (!contractAddress) {
      setError('The escrow contract is not ready yet. Please try again shortly.');

      return;
    }
    if (tooHigh) {
      setError(`The escrow holds ${format(remainingDeposit)} — a proposal above that is rejected.`);

      return;
    }

    const call = rentalContractCall('agreeSplit', rentierAmount);
    const done = await tx.run(
      (onProgress) =>
        walletUI.callContract({
          contractAddress,
          signature: call.signature,
          args: call.args,
          chainId: chain?.chainId,
          onProgress,
        }),
      'Transaction failed. Please try again.'
    );

    if (done) {
      setSuccess(true);
      onProposed?.();
    }
  };

  return (
    <Modal
      visible={visible}
      onClose={handleClose}
      icon={
        success ? (
          <CheckCircle size={64} color="white" weight="fill" />
        ) : (
          <Handshake size={64} color="white" weight="fill" />
        )
      }
      header={success ? 'Proposal published' : 'Propose a split'}
      buttonText={success ? 'Close' : isProcessing ? 'Processing…' : 'Publish proposal'}
      onSubmit={success ? handleClose : handleConfirm}
      disabled={isProcessing || (!success && !canSubmit)}
    >
      <Stack gap={3} width="100%">
        {!success ? (
          <>
            <Stack gap={1} className={styles.info}>
              <Row label="Deposit still held" value={format(remainingDeposit)} />
              <Typography color="white" fontWeight="600">
                The dealer keeps ({token.symbol})
              </Typography>
              <input
                type="number"
                min={0}
                step="any"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                className={styles.amountInput}
                disabled={isProcessing}
                placeholder="0"
              />
              <Row
                label="Comes back to you"
                value={rentierAmount !== null && !tooHigh ? format(remainingDeposit - rentierAmount) : '—'}
              />
            </Stack>

            {tooHigh ? (
              <Stack gap={1} className={styles.error}>
                <Typography color="white" variant="small">
                  The escrow only holds {format(remainingDeposit)}. A proposal above that is rejected by the contract.
                </Typography>
              </Stack>
            ) : null}

            <Stack gap={1} className={styles.warning}>
              <Stack flexDirection="row" gap={1} alignItems="center">
                <Warning size={20} color="white" weight="fill" />
                <Typography color="white" fontWeight="600">
                  Nothing moves until a second party agrees
                </Typography>
              </Stack>
              <Typography color="lightWhite" variant="small">
                Your proposal is published on-chain. The split executes the moment the dealer or GAC publishes the same
                number — and you can change your proposal until then. If nobody agrees within 14 days of the dispute
                starting, you can take the whole remaining deposit yourself.
              </Typography>
            </Stack>
          </>
        ) : (
          <Stack gap={1} className={styles.info}>
            <Typography color="white" fontWeight="600">
              Your proposal is on-chain. It executes as soon as one other party proposes the same split.
            </Typography>
          </Stack>
        )}

        <TxProgress
          state={tx.state}
          done={success}
          error={tx.error}
          // No wallet to sign with: `Connect Wallet` and the connection window stand in for Retry (app#315).
          connect={tx.needsWallet && chain ? <WalletButton family={chain.family} /> : null}
          explorerTxUrl={chain?.explorerTxUrl}
          onRetry={() => {
            tx.reset();
            handleConfirm();
          }}
        />

        {error ? (
          <Typography color="white" variant="small" className={styles.error}>
            {error}
          </Typography>
        ) : null}
      </Stack>
    </Modal>
  );
};

const Row: FC<{ label: string; value: string }> = ({ label, value }) => (
  <Stack flexDirection="row" justifyContent="space-between" gap={2}>
    <Typography color="lightWhite" variant="small">
      {label}
    </Typography>
    <Typography color="white" variant="small">
      {value}
    </Typography>
  </Stack>
);
